import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { FinanceService, ProjectService } from "../../services";
import { getExpenseTableColumns } from "../../pages/expense/constants";
import { DELETE_EXPENSE, EDIT_EXPENSE } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ExpenseList = ({ projectId = null }) => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projectsOfExpense, setProjectsOfExpense] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "expenses",
    defaults: { dropdowns: { categoryId: "" }, sortModel: [{ field: "date", sort: "desc" }] },
  });
  const selectedCategoryId = dropdowns.categoryId;
  const setSelectedCategoryId = (categoryId) => setDropdowns({ categoryId });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    expenseId: null,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditExpense = useHasPermission(EDIT_EXPENSE);
  const hasDeleteExpense = useHasPermission(DELETE_EXPENSE);

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      projectId,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      description: searchText,
    });
  }, [sortModel, paginationState, projectId, dateRange, selectedCategoryId, searchText]);

  const fetchData = async (expenseFilters = {}) => {
    setLoading(true);
    try {
      // Assuming finance categories will not go beyond 100
      const categoryResponse = await FinanceService.getCategories({ limit: 100 });
      setCategories(categoryResponse?.data?.rows);

      // to fetch expenses based on the available categories
      const categoryIds = categoryResponse?.data?.rows?.map((item) => item.id).join(",");

      if (categoryIds.length > 0) {
        const expenses = await FinanceService.getExpenses({
          ...expenseFilters,
          categoryIds: selectedCategoryId ? selectedCategoryId : categoryIds,
        });
        setExpenses(expenses.data.rows);
        setTotalCount(expenses.data.count);

        const projectIds = Array.from(new Set(expenses?.data?.rows?.map((item) => item.projectId)));
        if (!projectId && projectIds.length > 0) {
          const projectResponse = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjectsOfExpense(projectResponse?.data?.rows);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch expenses");
      console.error(`🚀 ~ fetchData ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Standardize selected dates to inclusive API boundaries.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Expenses" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Expenses",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleCategoryFilterChange = (value) => {
    setSelectedCategoryId(value ?? "");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleCategoryFilterClear = () => {
    setSelectedCategoryId("");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const onViewClick = (params, event) => {
    navigate(`/expense/${params.row.id}`);
  };

  const openDeleteModal = (expense) => {
    setDeleteModal({
      isOpen: true,
      expenseId: expense?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      expenseId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteExpense(deleteModal.expenseId);
      toast("Expense deleted successfully", { type: "success" });
      // WHY: Preserve current table filters (including selected date range) after delete refresh.
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        projectId,
        ...(dateRange && { fromDate: dateRange.startDate, toDate: dateRange.endDate }),
      });
    } catch (error) {
      toast("Failed to delete expense", { type: "error" });
      console.log(`🚀 ~ deleteExpense ~ error:`, error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (expenseData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/expense/edit/${expenseData.id}`);
        break;
      case "delete":
        openDeleteModal(expenseData);
        break;
      default:
        return;
    }
  };

  const columnHeaders = React.useMemo(
    () => getExpenseTableColumns(handleActions, hasEditExpense, hasDeleteExpense, !!projectId),
    [hasEditExpense, hasDeleteExpense, projectId],
  );

  const dropdownFilters = [
    {
      key: "categoryId",
      label: "Category",
      value: selectedCategoryId,
      onChange: handleCategoryFilterChange,
      onClear: handleCategoryFilterClear,
      options: categories.map((category) => ({ label: category.name, value: category.id })),
      multiple: false,
      // minWidth: 130,
    },
  ];

  const rows = expenses.map((expense, index) => {
    const project = projectsOfExpense.find((project) => project.id === expense.projectId);
    const category = categories.find((category) => category.id === expense.categoryId);
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: expense.id,
      date: moment(expense.date).format("DD MMM YYYY"),
      projectName: project?.name,
      description: expense.description || "",
      amount: Number(expense.amount),
      type: category?.name,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          dropdownFilters={dropdownFilters}
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount}
          loading={loading}
          sortModel={sortModel}
          defaultSortModel={DEFAULT_SORT}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this expense?"
      />
    </>
  );
};
